import React, { useEffect, useState, useCallback, useRef } from "react";
import { Row, Col } from "reactstrap";
import { Doughnut, Bar, HorizontalBar } from "react-chartjs-2";
import { getDashBoardData } from "../../util/APIUtils";
import "./Dashboard.css";
import moment from "moment";
import { LoadingOverlay } from "../../util/Helpers";
import DayWiseMocReportGrid from "../../components/CustomComponents/DayWiseMocReportGrid";

/* ── Design tokens ── */
const G = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const G_GREEN = "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)";
const G_ORANGE = "linear-gradient(135deg, #f7971e 0%, #f45c43 100%)";
const G_BLUE = "linear-gradient(135deg, #36a9e1 0%, #667eea 100%)";
const DARK = "#2d1b69";
const CARD_SHADOW = "0 4px 24px rgba(102,126,234,0.10)";
const CARD_HOVER = "0 10px 36px rgba(102,126,234,0.18)";
const PALETTE = ["#667eea", "#764ba2", "#11998e", "#f7971e", "#f45c43", "#36a9e1", "#38ef7d", "#56ab2f", "#eb3349", "#a8e063"];

const KPI_THEMES = [
  { accent: G, iconBg: "rgba(102,126,234,0.12)", iconClr: "#667eea" },
  { accent: G_BLUE, iconBg: "rgba(54,169,225,0.12)", iconClr: "#36a9e1" },
  { accent: G_GREEN, iconBg: "rgba(17,153,142,0.12)", iconClr: "#11998e" },
  { accent: G_ORANGE, iconBg: "rgba(247,151,30,0.12)", iconClr: "#f7971e" },
];

const fmt = (n) => Number(n).toLocaleString("en-IN");
const getInitMonthValue = () => {
  const today = moment();
  const targetMonth = today.date() >= 21 ? today.clone().add(1, "month") : today;

  return targetMonth.format("MMMM YYYY");
};

const Dashboard = () => {
  const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const [state, setState] = useState({
    values: {}, totBal: "", salsMainBal: [], expensesSmry: [],
    dayWiseMocreport: [],
    dashBoardConfig: [], selectVal: "", block: false,
    pickerOpen: false, pickerYear: moment().year(),
    showMarginValues: false,
  });
  const { values, totBal, salsMainBal, expensesSmry, dayWiseMocreport, dashBoardConfig, selectVal, block, pickerOpen, pickerYear, showMarginValues } = state;
  const pickerRef = useRef(null);

  useEffect(() => {
    const init = getInitMonthValue();
    setState((p) => ({ ...p, selectVal: init }));
    loadReqData(init);
  }, []);

  /* close picker on outside click */
  useEffect(() => {
    const handler = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setState((p) => ({ ...p, pickerOpen: false })); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const loadReqData = useCallback((m) => {
    setState((p) => ({ ...p, block: true }));
    getDashBoardData({ moc: m }).then((r) => {
      setState((p) => ({
        ...p, values: r.salesSummary, totBal: r.paymentDueSummary.totalBalance,
        salsMainBal: r.paymentDueSummary.salesManBalances, expensesSmry: r.expensesSummary,
        dayWiseMocreport: r.dayWiseMocreport || (r.salesSummary && r.salesSummary.dayWiseMocreport) || [],
        dashBoardConfig: r.DashboardConfig, block: false,
      }));
    });
  }, []);

  const pickMonth = (mIdx) => {
    const val = MONTHS[mIdx] + " " + pickerYear;
    setState((p) => ({ ...p, selectVal: val, pickerOpen: false }));
    loadReqData(val);
  };
  const handleRefresh = () => loadReqData(selectVal);
  const togglePicker = () => setState((p) => ({ ...p, pickerOpen: !p.pickerOpen, pickerYear: parseInt(p.selectVal.split(" ")[1]) || moment().year() }));
  const changePickerYear = (delta) => setState((p) => ({ ...p, pickerYear: p.pickerYear + delta }));
  const totalExp = () => expensesSmry.reduce((s, i) => s + i.totalAmount, 0);

  const toggleMarginValuesVisibility = () => {
    setState((p) => ({ ...p, showMarginValues: !p.showMarginValues }));
  };

  const marginVal = (key) => {
    const c = dashBoardConfig.find((x) => x.configKey === key);
    return c && !isNaN(parseFloat(c.configValue)) ? parseFloat(c.configValue) : 0;
  };
  const pc = (v) => (v ? parseFloat(v.substring(1).replace(/,/g, "")) || 0 : 0);

  const vkm = marginVal("VENDING_SALE_MARGIN"), skm = marginVal("SALES_PERSONS_SALE_MARGIN"), mcb = marginVal(values.mocName);
  const vs = pc(values.vendingSaleTotal), sp = pc(values.salesPersonsSaleTotal);
  const vc = pc(values.vendingTotalCuttings), sc = pc(values.salesMenTotalCuttings);
  const vsM = vs * vkm, spM = sp * skm, vcM = vc * vkm, scM = sc * skm;
  const paidVal = pc(values.paid), unpaidVal = pc(values.unpaid), dmgVal = pc(values.creditAdjustments);
  const netMargin = spM + vsM - vcM - scM + mcb - totalExp();
  const isAdmin = localStorage.getItem("access-role") === "ROLE_ADMIN";

  /* ── Chart configs ── */
  const salesChartData = {
    labels: ["Paid", "Unpaid", "Damages"],
    datasets: [{ data: [paidVal, unpaidVal, dmgVal], backgroundColor: ["#38ef7d", "#667eea", "#f45c43"], hoverBackgroundColor: ["#2dd36f", "#5568d4", "#eb3349"], borderWidth: 0 }],
  };
  const salesChartOpts = {
    cutoutPercentage: 72,
    legend: { position: "bottom", labels: { fontColor: "#555", padding: 18, usePointStyle: true, fontSize: 12 } },
    tooltips: { callbacks: { label: (t, d) => " " + d.labels[t.index] + ": \u20B9" + fmt(d.datasets[0].data[t.index]) } },
    maintainAspectRatio: false,
  };

  const colChartData = {
    labels: salsMainBal.map((s) => s.salesPersonName.split(" - ")[0]),
    datasets: [{
      label: "Due (lakhs)", barThickness: 22,
      data: salsMainBal.map((s) => { const r = typeof s.balance === "string" ? s.balance.replace(/[^\d.-]/g, "") : s.balance; return (parseFloat(r) || 0).toFixed(2); }),
      backgroundColor: salsMainBal.map((_, i) => PALETTE[i % PALETTE.length]),
    }],
  };
  const colChartOpts = {
    legend: { display: false },
    scales: {
      xAxes: [{ ticks: { beginAtZero: true, fontColor: "#999", fontSize: 11 }, gridLines: { color: "rgba(0,0,0,0.04)" }, afterFit: (s) => { s.paddingRight = 60; } }],
      yAxes: [{ ticks: { fontColor: "#555", fontSize: 11 }, gridLines: { display: false } }],
    },
    tooltips: { callbacks: { label: (t) => " " + t.xLabel + " lakhs" } },
    maintainAspectRatio: false,
  };
  const colBarPlugin = [{
    afterDatasetsDraw: (chart) => {
      const ctx = chart.ctx;
      chart.data.datasets.forEach((ds, di) => {
        const meta = chart.getDatasetMeta(di);
        meta.data.forEach((bar, idx) => {
          const val = ds.data[idx];
          ctx.save();
          ctx.fillStyle = DARK;
          ctx.font = "bold 11px sans-serif";
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(val + " L", bar._model.x + 6, bar._model.y);
          ctx.restore();
        });
      });
    },
  }];

  const expChartData = {
    labels: expensesSmry.map((e) => e.category),
    datasets: [{
      label: "Amount", barThickness: 28,
      data: expensesSmry.map((e) => e.totalAmount),
      backgroundColor: expensesSmry.map((_, i) => PALETTE[i % PALETTE.length]),
    }],
  };
  const expChartOpts = {
    legend: { display: false },
    scales: {
      xAxes: [{ ticks: { fontColor: "#999", fontSize: 10, maxRotation: 45, minRotation: 20 }, gridLines: { display: false } }],
      yAxes: [{ ticks: { beginAtZero: true, fontColor: "#999", fontSize: 11 }, gridLines: { color: "rgba(0,0,0,0.05)" } }],
    },
    tooltips: { callbacks: { label: (t) => " \u20B9" + fmt(t.yLabel) } },
    maintainAspectRatio: false,
  };

  /* ── Data ── */
  const kpis = [
    { title: "Total Invoice", val: values.totalSale, icon: "fa fa-file-text-o" },
    { title: "Total Bill", val: values.totalBillSale, icon: "fa fa-credit-card" },
    { title: "Paid", val: values.paid, icon: "fa fa-check-circle" },
    { title: "Unpaid", val: values.unpaid, icon: "fa fa-exclamation-circle" },
  ];
  const salesRows = [
    { l: "Vending Sales", v: values.vendingSaleTotal },
    { l: "Sales Person", v: values.salesPersonsSaleTotal },
    { l: "SP Total Cutting", v: values.salesMenTotalCuttings },
    { l: "Vending Total Cutting", v: values.vendingTotalCuttings },
    { l: "Damages", v: values.creditAdjustments },
    { l: "Outpay Adj", v: values.outPayAdjustments },
    { l: "Total Cancelled", v: values.totalCancelledSales },
    { l: "Total Echo", v: values.totalEchoSales },
    { l: "Total Dump", v: values.totalDumpSales },
  ];
  const mRows = [
    { l: "Vending Sales", s: vs, p: vkm, a: vsM.toFixed(2) },
    { l: "Sales Person", s: sp, p: skm, a: spM.toFixed(2) },
    { l: "Vending Cuttings", s: -vc, p: vkm, a: (-vcM).toFixed(2) },
    { l: "SP Cuttings", s: -sc, p: skm, a: (-scM).toFixed(2) },
    { l: "Grand Total", hl: true, s: (vs + sp - vc - sc).toFixed(2), p: "", a: (spM + vsM - vcM - scM).toFixed(2) },
    { l: "Total Expenses", s: "", p: "", a: (-totalExp()).toFixed(2) },
    { l: "Bonus", s: "", p: "", a: mcb },
    { l: "Net Value", hl: true, s: "", p: "", a: netMargin.toFixed(2) },
  ];

  /* ─────────────── RENDER ─────────────── */
  return (
    <div className="db-wrap">
      {block && <LoadingOverlay />}

      {/* ── TOP BAR ── */}
      <div className="db-topbar">
        <div className="db-topbar-l">
          <span className="db-topbar-ico"><i className="fa fa-tachometer" /></span>
          <span className="db-topbar-ttl">Dashboard</span>
          <span className="db-topbar-mo">{values.mocName || selectVal}</span>
        </div>
        <div className="db-topbar-r">
          <div className="db-picker-wrap" ref={pickerRef}>
            <button className="db-mo-btn" onClick={togglePicker}>
              {selectVal} <i className="fa fa-caret-down" style={{ marginLeft: 6 }} />
            </button>
            {pickerOpen && (
              <div className="db-picker">
                <div className="db-picker-hdr">
                  <button className="db-picker-arr" onClick={() => changePickerYear(-1)}><i className="fa fa-chevron-left" /></button>
                  <span className="db-picker-yr">{pickerYear}</span>
                  <button className="db-picker-arr" onClick={() => changePickerYear(1)}><i className="fa fa-chevron-right" /></button>
                </div>
                <div className="db-picker-grid">
                  {MONTHS.map((m, i) => {
                    const sel = selectVal === m + " " + pickerYear;
                    return <button key={i} className={"db-picker-mo" + (sel ? " db-picker-sel" : "")} onClick={() => pickMonth(i)}>{m.substring(0, 3)}</button>;
                  })}
                </div>
              </div>
            )}
          </div>
          <button className="db-refresh" onClick={handleRefresh} title="Refresh"><i className="fa fa-refresh" /></button>
        </div>
      </div>

      {/* ── KPI CARDS ── */}
      <Row className="db-kpi-row">
        {kpis.map((k, i) => (
          <Col xs="6" lg="3" key={i}>
            <div className="db-kpi" style={{ borderLeft: "5px solid transparent", borderImage: KPI_THEMES[i].accent + " 1" }}>
              <div className="db-kpi-ico" style={{ background: KPI_THEMES[i].iconBg, color: KPI_THEMES[i].iconClr }}>
                <i className={k.icon} />
              </div>
              <div className="db-kpi-txt">
                <div className="db-kpi-lbl">{k.title}</div>
                <div className="db-kpi-val">{k.val || "--"}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* ── CHARTS ── */}
      <Row>
        <Col lg="5">
          <div className="db-card">
            <div className="db-card-hdr">
              <span className="db-card-ttl"><i className="fa fa-pie-chart" style={{ marginRight: 8, opacity: 0.5 }} />Sales Breakdown</span>
            </div>
            <div className="db-card-body" style={{ position: "relative" }}>
              <div style={{ height: 290 }}>
                <Doughnut data={salesChartData} options={salesChartOpts} />
              </div>
              <div className="db-donut-center">
                <span className="db-donut-sub">Total</span>
                <span className="db-donut-val">{values.totalSale || "--"}</span>
              </div>
            </div>
          </div>
        </Col>
        <Col lg="7">
          <div className="db-card">
            <div className="db-card-hdr">
              <span className="db-card-ttl"><i className="fa fa-bar-chart" style={{ marginRight: 8, opacity: 0.5 }} />Market Collection Due</span>
              <span className="db-badge">{totBal}</span>
            </div>
            <div className="db-card-body">
              <div style={{ height: 290 }}>
                {salsMainBal.length > 0
                  ? <HorizontalBar data={colChartData} options={colChartOpts} plugins={colBarPlugin} />
                  : <div className="db-empty">No collection data</div>}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ── SALES DETAILS GRID ── */}
      <div className="db-card">
        <div className="db-card-hdr">
          <span className="db-card-ttl"><i className="fa fa-list-ul" style={{ marginRight: 8, opacity: 0.5 }} />Sales Details</span>
        </div>
        <div className="db-card-body">
          <div className="db-grid">
            {salesRows.map((r, i) => (
              <div className="db-grid-item" key={i} style={{ borderLeftColor: PALETTE[i % PALETTE.length] }}>
                <div className="db-grid-lbl">{r.l}</div>
                <div className="db-grid-val">{r.v || "--"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DayWiseMocReportGrid data={dayWiseMocreport} mocLabel={values.mocName || selectVal} />

      {/* ── ADMIN: EXPENSES + MARGIN ── */}
      {isAdmin && (
        <Row>
          <Col lg="6">
            <div className="db-card">
              <div className="db-card-hdr">
                <span className="db-card-ttl"><i className="fa fa-money" style={{ marginRight: 8, opacity: 0.5 }} />Expenses</span>
                <span className="db-badge">{"\u20B9" + fmt(totalExp())}</span>
              </div>
              <div className="db-card-body">
                <div style={{ height: 260, marginBottom: 18 }}>
                  {expensesSmry.length > 0 ? <Bar data={expChartData} options={expChartOpts} /> : <div className="db-empty">No expense data</div>}
                </div>
                <div className="db-exp-list">
                  {expensesSmry.map((e, i) => (
                    <div className="db-exp-row" key={i}>
                      <span className="db-exp-dot" style={{ background: PALETTE[i % PALETTE.length] }} />
                      <span className="db-exp-cat">{e.category}</span>
                      <span className="db-exp-amt">{"\u20B9" + fmt(e.totalAmount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Col>
          <Col lg="6">
            <div className="db-card">
              <div className="db-card-hdr">
                <span className="db-card-ttl"><i className="fa fa-calculator" style={{ marginRight: 8, opacity: 0.5 }} />Margin Analysis</span>
                <span className={"db-badge " + (netMargin >= 0 ? "db-badge-green" : "db-badge-red")}>
                  Net: {showMarginValues ? "\u20B9" + fmt(netMargin.toFixed(2)) : "****"}
                </span>
                <button
                  onClick={toggleMarginValuesVisibility}
                  title={showMarginValues ? "Hide values" : "Show values"}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "18px",
                    color: "#667eea",
                    marginLeft: "10px",
                  }}
                >
                  <i className={showMarginValues ? "fa fa-eye-slash" : "fa fa-eye"} />
                </button>
              </div>
              <div className="db-card-body">
                <table className="db-mtbl">
                  <thead>
                    <tr>
                      <th className="db-mtbl-th db-mtbl-th-first">Metric</th>
                      <th className="db-mtbl-th db-mtbl-th-r">Total Sale</th>
                      <th className="db-mtbl-th db-mtbl-th-r">Margin %</th>
                      <th className="db-mtbl-th db-mtbl-th-r db-mtbl-th-last">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mRows.map((r, i) => (
                      <tr key={i} className={r.hl ? "db-mtbl-hl" : ""}>
                        <td className="db-mtbl-td db-mtbl-td-name">{r.l}</td>
                        <td className="db-mtbl-td db-mtbl-td-r">{r.s !== "" ? fmt(r.s) : ""}</td>
                        <td className="db-mtbl-td db-mtbl-td-r">
                          {r.p !== "" ? (showMarginValues ? r.p : "****") : ""}
                        </td>
                        <td className="db-mtbl-td db-mtbl-td-r">
                          {r.a !== "" ? (showMarginValues ? fmt(r.a) : "****") : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Dashboard;



// WEBPACK FOOTER //
// ./src/views/Dashboard/Dashboard.js